/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

// Shared visibility logic. A monster's effective state is derived (never stored) from three
// independent fields:
//   - desiredVisibility: what the OWNER wants ('Private' | 'Shared' | 'Public').
//   - approval:          the MODERATOR's grant for the current content (null until approved).
//   - violationSummary:  counts of outstanding violation reports ({ open, confirmed }).
//   - flaggedForReview:  set true once a violation/copyright report (or a rejection) is filed,
//                        cleared only when a moderator re-approves. While set, the monster is hidden
//                        from the public even by direct link — so an owner can't self-resolve a
//                        report and silently restore visibility without re-vetting.
//
// Rules:
//   - Link access (getPublicMonster): not Private AND no outstanding violations AND not
//     flagged-for-review. Shared and Public are identical for link access.
//   - Community listing (browse/search): Public AND approved-for-Public AND no outstanding violations.
//   - Editable: only while Private.
// NOTE: a single outstanding report hides the monster until a moderator resolves it.

export type DesiredVisibility = 'Private' | 'Shared' | 'Public';
export type ApprovalLevel = 'Shared' | 'Public';

export interface Approval {
  level: ApprovalLevel;
  by: string;
  at: any; // Firestore Timestamp (server) or serialized value (client) — not inspected here.
}

export interface ViolationSummary {
  open: number;      // unadjudicated user reports
  confirmed: number; // moderator-confirmed violations / rejections
}

export function violationSummaryOf(summary?: ViolationSummary | null): ViolationSummary {
  return { open: summary?.open || 0, confirmed: summary?.confirmed || 0 };
}

export function hasOutstandingViolations(summary?: ViolationSummary | null): boolean {
  const s = violationSummaryOf(summary);
  return s.open > 0 || s.confirmed > 0;
}

// Returned by getPublicMonster (link access).
export function isLinkVisible(desired: DesiredVisibility, summary?: ViolationSummary | null, flaggedForReview?: boolean): boolean {
  return desired !== 'Private' && !hasOutstandingViolations(summary) && flaggedForReview !== true;
}

// Appears in the public community list / monster search.
export function isCommunityListed(desired: DesiredVisibility, approval?: Approval | null, summary?: ViolationSummary | null): boolean {
  return desired === 'Public' && !!approval && approval.level === 'Public' && !hasOutstandingViolations(summary);
}

// Whether the existing approval grant already covers the owner's desired level.
export function isApprovedForDesired(desired: DesiredVisibility, approval?: Approval | null): boolean {
  if (desired === 'Private') return true;
  if (!approval) return false;
  if (desired === 'Public') return approval.level === 'Public';
  return approval.level === 'Shared' || approval.level === 'Public'; // Shared
}

// The owner can only edit a Private monster.
export function isEditable(desired: DesiredVisibility): boolean {
  return desired === 'Private';
}

// In the moderator approval queue: a sharing request not yet approved at the desired level, with no
// outstanding reports (reported monsters live in the reported queue instead).
export function isAwaitingApproval(desired: DesiredVisibility, approval?: Approval | null, summary?: ViolationSummary | null): boolean {
  if (desired === 'Private') return false;
  if (hasOutstandingViolations(summary)) return false;
  return !isApprovedForDesired(desired, approval);
}
